import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { QuickKeyKey } from '../utils/qk-types'
import { QuickKeyNodeType } from '../utils/qk.constants'
import { useImageURLForQuickKey } from '../useImageURLForQuickKey'
import { getClassesForStatus, HandleDropParams } from '../quickKeyUtils'
import { useDragEvents } from '../useDragEvents'

interface QKKeyProps {
  quickKey: QuickKeyKey
  setFocus: (node: QuickKeyKey) => void
  isFocussed?: boolean
  onClick: (qk: QuickKeyKey) => void
  handleDrop?: (params: HandleDropParams) => void
}

export const QKKey: FunctionComponent<QKKeyProps> = ({
  quickKey,
  isFocussed,
  setFocus,
  onClick,
  handleDrop,
}) => {
  const imageURL = useImageURLForQuickKey(quickKey)

  const [hover, setHover] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)

  const dragEvents = useDragEvents({
    quickKey,
    handleDrop,
    containerElement: containerRef.current,
  })

  useEffect(() => {
    if (containerRef.current && isFocussed) {
      containerRef.current.focus()
    }
  }, [isFocussed])

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={() => setFocus(quickKey)}
      onClick={() => onClick(quickKey)}
      ref={containerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isFocussed ? 0 : undefined}
      className={classNames(
        'vd-quick-key',
        { 'vd-quick-key-grid--dragging': handleDrop },
        `vd-quick-key--${quickKey.viewData.color}`,
        `vd-quick-key--${QuickKeyNodeType.QUICK_KEY}`,
        { 'vd-quick-key--hover': hover }
      )}
      draggable={Boolean(handleDrop)}
      onDragStart={dragEvents.onDragStart}
      onDragEnter={dragEvents.onDragEnter}
      onDragLeave={dragEvents.onDragLeave}
      onDragOver={dragEvents.onDragOver}
      onDrop={dragEvents.onDrop}
    >
      <div
        className={classNames(
          'vd-quick-key-inner',
          {
            'vd-quick-key-inner--with-image': imageURL,
          },
          ...getClassesForStatus(dragEvents.dragStatus)
        )}
        data-product-id={quickKey.node.product_id}
      >
        {imageURL && (
          <div
            className="vd-quick-key-image"
            style={{ backgroundImage: `url(${imageURL})` }}
          />
        )}
        <span className="vd-quick-key-label">{quickKey.viewData.label}</span>
      </div>
    </div>
  )
}
